import { Link } from '@tanstack/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { styled } from '@linaria/react'
import { useModal } from '@ebay/nice-modal-react'
import useMyProfileCategories from '../../hooks/useMyProfileCategories'
import Icon from '../icon'
import ToastMessage from '../common/toast-message'
import UnsuccessfulVerificationModal from '../kyc/unsuccessful-verification-modal'
import { useMemo } from 'react'
import RequestPasswordResetModal from '../common/request-password-reset-modal'
import { isCaptureSupported } from '../../utils/device'

enum Profile {
  PERSONAL_INFORMATION,
  VERIFICATION_KYC,
  CHANGE_PASSWORD,
}

const Container = styled.div`
  transition: all 0.2s ease-in-out;
  overflow: hidden;
  .theme-dark & {
    border-bottom: 1px solid #1f223c;
  }
  .theme-light & {
    border-bottom: 1px solid #f5f6f9;
  }
`

const Head = styled.div`
  padding: 10px 20px;
  svg {
    transition: all 0.2s ease-in-out;
  }
  span {
    font-size: 12px;
    text-transform: uppercase;
    color: var(--txt);
  }
  img {
    max-width: 25px;
    max-height: 25px;
  }
`

const DropdownItem = styled.div`
  line-height: 35px;
  &:last-child {
    border: none;
  }
  span {
    font-size: 12px;
    text-transform: uppercase;
    color: var(--txt);
  }
  div {
    width: 40px;
  }
  svg {
    width: 25px;
    height: 25px;

    fill: var(--txt);
  }
  &.active {
    span {
      color: #46aef7;
    }
    svg {
      fill: #46aef7;
    }
  }
`
const DropdownContainer = styled.div`
  padding: 6px 0 6px 20px;
  background-color: var(--secondary);
`

const TabLabel = styled.p`
  margin-left: 10px;
  margin-right: 10px;
  color: var(--highlight);
`

const VerifiedBadge = styled.p`
  color: #fff;
  font-size: 9px;
  font-weight: 400;
  background-color: #58d66d;
  padding: 0 7px;
  border-radius: 12px;
  line-height: 24px;
  &.unverified {
    background-color: #515151;
  }
`

type MyProfileDrawerTabsProps = {
  closeProfileDrawer: () => void
  is_verified: number
  verif_request_count: number
}

const MyProfileDrawerTabs = ({
  closeProfileDrawer,
  is_verified,
  verif_request_count,
}: MyProfileDrawerTabsProps) => {
  const { t } = useTranslation('drawer')
  const [isOpen, setIsOpen] = useState(false)
  const { categories } = useMyProfileCategories()
  const toastMessage = useModal(ToastMessage)
  const unsuccessfulVerificationModal = useModal(UnsuccessfulVerificationModal)
  const requestPasswordResetModal = useModal(RequestPasswordResetModal)

  const kycBadge = useMemo(() => {
    if (is_verified === 0) {
      return (
        <VerifiedBadge style={{ backgroundColor: '#515151' }}>
          {t('verify', { ns: 'kyc' })}
        </VerifiedBadge>
      )
    } else if (is_verified === 1) {
      return (
        <VerifiedBadge style={{ backgroundColor: '#58D66D' }}>
          {t('verified', { ns: 'kyc' })}
        </VerifiedBadge>
      )
    } else if (is_verified === 2) {
      return (
        <VerifiedBadge style={{ backgroundColor: '#E9BF2E' }}>
          {t('in-review', { ns: 'kyc' })}
        </VerifiedBadge>
      )
    } else if (is_verified === 3) {
      return (
        <VerifiedBadge style={{ backgroundColor: '#E74242' }}>
          {t('declined', { ns: 'kyc' })}
        </VerifiedBadge>
      )
    }

    return null
  }, [is_verified])

  return (
    <Container
      style={{
        height: isOpen ? categories.length * 35 + 51 : '39px',
      }}>
      <Head
        className="flx-btw-ctr"
        onClick={() => setIsOpen(prev => (prev ? false : true))}>
        <div className="flx-str-ctr">
          <TabLabel>MI PERFIL</TabLabel>
        </div>
        <Icon
          id="triangle-down"
          width="16"
          height="10"
          fill="#46aef7"
          style={{ transform: `rotate(${isOpen ? '-180' : '0'}deg)` }}
        />
      </Head>
      <DropdownContainer>
        {categories.map(category => (
          <Link to="/" key={category.value}>
            <DropdownItem onClick={closeProfileDrawer}>
              {category.value === Profile.PERSONAL_INFORMATION ? (
                <Link to="/profile">
                  <TabLabel>{category.label}</TabLabel>
                </Link>
              ) : category.value === Profile.VERIFICATION_KYC ? (
                <Link
                  to={
                    [3].includes(is_verified) ||
                    (verif_request_count > 3 && +is_verified === 3) ||
                    !isCaptureSupported()
                      ? undefined
                      : '/profile/kyc'
                  }>
                  <TabLabel
                    className="flx-btw-ctr"
                    onClick={() => {
                      if (!isCaptureSupported()) {
                        toastMessage.show({
                          text: 'Para completar tu verificación y tener una mejor experiencia, es necesario acceder a la plataforma de juego en Chrome o Safari',
                        })
                        return
                      }

                      if (verif_request_count >= 3 && +is_verified === 3) {
                        toastMessage.show({
                          text: t('contact-customer-service', { ns: 'kyc' }),
                        })
                        return
                      }
                      if (is_verified === 3) {
                        unsuccessfulVerificationModal.show()
                        closeProfileDrawer()
                        return
                      }

                      closeProfileDrawer()
                    }}>
                    Verificación de Usuario
                    {kycBadge}
                  </TabLabel>
                </Link>
              ) : (
                <TabLabel
                  onClick={() => {
                    requestPasswordResetModal.show()
                  }}>
                  {category.label}
                </TabLabel>
              )}
            </DropdownItem>
          </Link>
        ))}
      </DropdownContainer>
    </Container>
  )
}

export default MyProfileDrawerTabs
